import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import { api } from "@/lib/api";
import { TEST_IDS } from "@/testIds";

const REQUIRED_SHEETS = ["Sales", "Milestones", "Land Sales", "Ongoing Projects"];

export default function Upload() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please upload an .xlsx file");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File too large (max 20MB)");
      return;
    }
    setFile(f);
    setSummary(null);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSummary(data);
      toast.success("Workbook imported", {
        description: `${data.projects} projects · ${data.sales} sales rows · ${data.flags_generated} flags`,
      });
    } catch (e) {
      toast.error("Import failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell testId="upload-page" kicker="Data Ingestion" title="Excel Import">
      <p className="-mt-4 text-muted-dark text-sm max-w-2xl">
        Drop the Radiance collections workbook to refresh sales, milestones,
        land fees and ongoing snapshots. Existing data will be replaced.
      </p>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          pick(e.dataTransfer.files?.[0]);
        }}
        className={`cf-card cf-fade-up p-10 md:p-14 text-center transition-all duration-150 ${dragOver ? "ring-2 ring-ink/20" : ""}`}
      >
        <UploadCloud size={40} className="mx-auto text-muted-light mb-4" strokeWidth={1.5} />
        <div className="font-display text-lg text-ink mb-1">
          {file ? file.name : "Drop your .xlsx here"}
        </div>
        <div className="text-xs text-muted-dark mb-6">
          Required sheets: {REQUIRED_SHEETS.join(", ")}
        </div>
        <input
          ref={inputRef}
          data-testid={TEST_IDS.uploadInput}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={(e) => pick(e.target.files?.[0])}
        />
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => inputRef.current?.click()}
            className="px-4 py-2 text-sm border border-hairline rounded-full text-ink hover:border-ink"
          >
            Choose file
          </button>
          <button
            data-testid={TEST_IDS.uploadSubmit}
            onClick={submit}
            disabled={!file || busy}
            className="px-5 py-2 text-sm rounded-full bg-ink text-paper hover:bg-ink/90 disabled:opacity-50"
          >
            {busy ? "Importing…" : "Import workbook"}
          </button>
        </div>
      </div>

      {summary && (
        <div
          data-testid={TEST_IDS.uploadSummary}
          className="cf-card cf-fade-up p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 size={16} className="text-ink" />
            <span className="font-display text-lg text-ink">Import complete</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Cell label="Projects" value={summary.projects} />
            <Cell label="Sales rows" value={summary.sales} />
            <Cell label="Milestones" value={summary.milestones} />
            <Cell label="Land-fee lines" value={summary.land_fees} />
            <Cell label="Ongoing snapshots" value={summary.ongoing_snapshots} />
            <Cell label="Flags generated" value={summary.flags_generated} tone="gold" />
          </div>
          {summary.warnings?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-hairline">
              <div className="text-xs uppercase tracking-widest text-muted-light mb-2">Warnings</div>
              <ul className="text-xs text-red-600 space-y-1 list-disc pl-4">
                {summary.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="cf-card p-6 flex gap-4 text-xs text-muted-dark">
        <FileSpreadsheet size={20} className="text-muted-light shrink-0" strokeWidth={1.5} />
        <div className="leading-relaxed">
          <strong className="text-ink font-medium">Ground-truth format:</strong> the projection
          engine reads only the four sheets listed above. The <em>Collections Model</em> tab is
          reference only — not re-imported. All figures are ₹ Lakhs.
        </div>
      </div>
    </PageShell>
  );
}

function Cell({ label, value, tone }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-light mb-1">{label}</div>
      <div className={`num text-2xl font-medium ${tone === "gold" ? "gradient-text" : "text-ink"}`}>
        {value}
      </div>
    </div>
  );
}
